/* Copyright (c) 2021, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

export const KbModifierKeys: number[] = [
    0x10,
    0xa0,
    0xa1, // Generic/Left/Right Shift
    0x11,
    0xa2,
    0xa3, // Generic/Left/Right Ctrl
    0x12,
    0xa4,
    0xa5, // Generic/Left/Right Alt
    0x5b,
    0x5c, // Left/Right Meta,
    0xff37 // fake cmd
];

export const KbNormalizedModifierKeys = {
    0xa0: 0x10, // Left Shift
    0xa1: 0x10, // Right Shift
    0xa2: 0x11, // Left Ctrl
    0xa3: 0x11, // Right Ctrl
    0xa4: 0x12, // Left Alt
    0xa5: 0x12 // Right Alt
};

export const KbNonPrintableKeyMap = {
    0x00: 'None',
    0x08: 'Backsp',
    0x09: 'Tab',
    0x10: 'Shift', // Either shift
    0x11: 'Ctrl', // Either control
    0x12: 'Alt', // Either alt
    0x0c: 'Clear',
    0x0d: 'Enter',
    0x13: 'Pause',
    0x14: 'Caps',
    0x1b: 'Esc',
    0x20: 'Space',
    0x21: 'PgUp',
    0x22: 'PgDown',
    0x23: 'End',
    0x24: 'Home',
    0x25: 'Left',
    0x26: 'Up',
    0x27: 'Right',
    0x28: 'Down',
    0x29: 'Select',
    0x2a: 'Print',
    0x2b: 'Exec',
    0x2c: 'PrtScn',
    0x2d: 'Insert',
    0x2e: 'Delete',
    0x2f: 'Help',
    0x5b: 'Win', // Left Win
    0x5c: 'Win', // Right Win
    0x5d: 'Apps',
    0x5f: 'Sleep',
    0x60: 'Num0',
    0x61: 'Num1',
    0x62: 'Num2',
    0x63: 'Num3',
    0x64: 'Num4',
    0x65: 'Num5',
    0x66: 'Num6',
    0x67: 'Num7',
    0x68: 'Num8',
    0x69: 'Num9',
    0x6a: 'Num*',
    0x6b: 'Num+',
    0x6c: 'Separ', // SEPARATOR
    0x6d: 'Num-',
    0x6e: 'Num.',
    0x6f: 'Num/',
    0x70: 'F1',
    0x71: 'F2',
    0x72: 'F3',
    0x73: 'F4',
    0x74: 'F5',
    0x75: 'F6',
    0x76: 'F7',
    0x77: 'F8',
    0x78: 'F9',
    0x79: 'F10',
    0x7a: 'F11',
    0x7b: 'F12',
    0x7c: 'F13',
    0x7d: 'F14',
    0x7e: 'F15',
    0x7f: 'F16',
    0x80: 'F17',
    0x81: 'F18',
    0x82: 'F19',
    0x83: 'F20',
    0x84: 'F21',
    0x85: 'F22',
    0x86: 'F23',
    0x87: 'F24',
    0x90: 'NumLck',
    0x91: 'ScrLck',
    0xa0: 'Shift', // Left shift
    0xa1: 'Shift', // Right shift
    0xa2: 'Ctrl', // Left control
    0xa3: 'Ctrl', // Right control
    0xa4: 'Alt', // Left alt
    0xa5: 'Alt', // Right alt

    /**
     * HACK!
     * Easy fix to enabling mapping from Unicode keycodes in the US-102
     * keyboard to Unicode characters.
     * This is NOT a proper way to do localization.
     */
    0xba: ';',
    0xbb: '=',
    0xbc: ',',
    0xbd: '-',
    0xbe: '.',
    0xbf: '/',
    0xc0: '`',
    0xdb: '[',
    0xdc: '\\',
    0xdd: ']',
    0xde: "'",

    0xff37: 'Cmd'
};

export const SpecialKeysToCodeMap = {
    Backspace: 0x08,
    Tab: 0x09,
    Shift: 0x10,
    Control: 0x11,
    Alt: 0x12,
    Clear: 0x0c,
    Enter: 0x0d,
    Pause: 0x13,
    CapsLock: 0x14,
    Escape: 0x1b,
    Space: 0x20,
    PageUp: 0x21,
    PageDown: 0x22,
    End: 0x23,
    Home: 0x24,
    ArrowLeft: 0x25,
    ArrowUp: 0x26,
    ArrowRight: 0x27,
    ArrowDown: 0x28,
    Select: 0x29,
    Print: 0x2a,
    Execute: 0x2b,
    PrintScreen: 0x2c,
    Insert: 0x2d,
    Delete: 0x2e,
    Help: 0x2f,
    Win: 0x5b,
    ContextMenu: 0x5d,
    Standby: 0x5f,
    F1: 0x70,
    F2: 0x71,
    F3: 0x72,
    F4: 0x73,
    F5: 0x74,
    F6: 0x75,
    F7: 0x76,
    F8: 0x77,
    F9: 0x78,
    F10: 0x79,
    F11: 0x7a,
    F12: 0x7b,
    F13: 0x7c,
    F14: 0x7d,
    F15: 0x7e,
    F16: 0x7f,
    F17: 0x80,
    F18: 0x81,
    F19: 0x82,
    F20: 0x83,
    F21: 0x84,
    F22: 0x85,
    F23: 0x86,
    F24: 0x87,
    NumLock: 0x90,
    ScrollLock: 0x91,
    /*
    0x60: 'Num0',
    0x61: 'Num1',
    0x62: 'Num2',
    0x63: 'Num3',
    0x64: 'Num4',
    0x65: 'Num5',
    0x66: 'Num6',
    0x67: 'Num7',
    0x68: 'Num8',
    0x69: 'Num9',
    0x6a: 'Num*',
    0x6b: 'Num+',
    0x6c: 'Separ', // SEPARATOR
    0x6d: 'Num-',
    0x6e: 'Num.',
    0x6f: 'Num/',
*/
    /**
     * HACK!
     * Easy fix to enabling mapping from Unicode keycodes in the US-102
     * keyboard to Unicode characters.
     * This is NOT a proper way to do localization.
     */
    ';': 0xba,
    '=': 0xbb,
    ',': 0xbc,
    '-': 0xbd,
    '.': 0xbe,
    '/': 0xbf,
    '`': 0xc0,
    '[': 0xdb,
    '\\': 0xdc,
    ']': 0xdd,
    "'": 0xde,

    Cmd: 0xff37
};
